/**
 * Runtime loader for **lib-jitsi-meet** (`window.JitsiMeetJS`) — the FULL Jitsi
 * client library (not just the iframe external API). Only the `/share` laptop
 * remote needs it: it captures the laptop screen and pipes it into the room's
 * meeting over a `ProxyConnectionService` P2P link (the legacy spot share flow).
 * The in-room control iPad never loads this — it drives the TV over the DO only.
 *
 * Like {@link ../meeting/external-api}, the script is loaded at RUNTIME from the
 * meeting deployment (NOT bundled — lib-jitsi-meet is large and bundler-hostile).
 * The URL is derived from the configured `externalApiSrc` (its canonical sibling
 * is `…/libs/lib-jitsi-meet.min.js`), so no new hostname is hardcoded.
 */

import { log } from '../log.js';

/** Derive the lib-jitsi-meet URL from the external_api.js URL (canonical sibling). */
export function libJitsiSrcFrom(externalApiSrc: string): string {
    // e.g. https://alpha.jitsi.net/external_api.js -> https://alpha.jitsi.net/libs/lib-jitsi-meet.min.js
    if (externalApiSrc.endsWith('/external_api.js')) {
        return externalApiSrc.replace(/\/external_api\.js$/, '/libs/lib-jitsi-meet.min.js');
    }

    // Fall back to the deployment origin if the URL shape is unexpected.
    const url = new URL(externalApiSrc);

    return `${url.origin}/libs/lib-jitsi-meet.min.js`;
}

let libLoad: Promise<void> | null = null;
let initialized = false;

/** Load lib-jitsi-meet once per document and `init()` it (idempotent). */
export function loadLibJitsiMeet(externalApiSrc: string): Promise<void> {
    if (window.JitsiMeetJS) {
        ensureInit();

        return Promise.resolve();
    }

    if (libLoad) {
        return libLoad;
    }

    const src = libJitsiSrcFrom(externalApiSrc);

    libLoad = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');

        script.src = src;
        script.async = true;
        script.onload = () => {
            if (!window.JitsiMeetJS) {
                reject(new Error('lib-jitsi-meet loaded but window.JitsiMeetJS is missing'));

                return;
            }
            ensureInit();
            resolve();
        };
        script.onerror = () => {
            libLoad = null;
            reject(new Error(`failed to load lib-jitsi-meet from ${src}`));
        };
        document.head.appendChild(script);
    });

    return libLoad;
}

/** Quiet, single `JitsiMeetJS.init()` (the library is noisy by default). */
function ensureInit(): void {
    if (initialized || !window.JitsiMeetJS) {
        return;
    }

    try {
        window.JitsiMeetJS.setLogLevel?.('error');
        window.JitsiMeetJS.init({});
        initialized = true;
        log.log('lib-jitsi-meet initialised');
    } catch (err) {
        log.error('lib-jitsi-meet init failed', { error: String(err) });
        throw err;
    }
}
